/**
 * COPIED from pages/dev-agent/components/metadataEditor/SaveErrorsBanner.tsx.
 * No store coupling — `SaveErrorState` type now resolves to the local
 * multi-agent sessionSaveWithValidation copy.
 */
import { AlertCircle, Copy } from "lucide-react";
import toast from "react-hot-toast";
import type { SaveErrorState } from "./sessionSaveWithValidation";

export function SaveErrorsBanner({
  saveErrors,
  isSaving,
  onDismiss,
  onStop,
}: {
  saveErrors: NonNullable<SaveErrorState>;
  isSaving: boolean;
  onDismiss: () => void;
  onStop?: () => void;
}) {
  const inProgress =
    saveErrors.message.includes("Fixing") ||
    saveErrors.message.includes("Validating") ||
    saveErrors.message.includes("Re-validating");
  const success = saveErrors.message.includes("Fixed and saved");
  // Saved-but-unvalidated (new class + validator down): a non-blocking amber
  // notice, NOT a red compile error. Driven by an explicit severity flag.
  const warning = saveErrors.severity === "warning";
  const amber = inProgress || warning;
  const bgClass = amber
    ? "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50"
    : success
      ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900/50"
      : "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/50";
  const textClass = amber
    ? "text-amber-800 dark:text-amber-300"
    : success
      ? "text-green-800 dark:text-green-300"
      : "text-red-800 dark:text-red-300";
  const labelClass = amber
    ? "text-amber-700 dark:text-amber-400"
    : success
      ? "text-green-700 dark:text-green-400"
      : "text-red-700 dark:text-red-400";

  if (inProgress || success || warning) {
    return (
      <div
        className={`flex items-center justify-between gap-2 px-4 py-2 border-b shrink-0 ${bgClass}`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {inProgress && (
            <div className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-amber-600 border-t-transparent shrink-0" />
          )}
          <span className={`text-xs truncate ${textClass}`}>
            {saveErrors.message}
          </span>
        </div>
        {inProgress && onStop && (
          <button
            type="button"
            onClick={onStop}
            className="text-xs px-2.5 py-0.5 rounded font-medium shrink-0 border border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40"
          >
            Stop
          </button>
        )}
        {!isSaving && (
          <button
            type="button"
            onClick={onDismiss}
            className={`text-xs px-2 py-0.5 rounded hover:bg-muted/50 shrink-0 ${labelClass}`}
          >
            Dismiss
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`px-4 py-3 border-b shrink-0 ${bgClass}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <AlertCircle className={`h-4 w-4 shrink-0 ${labelClass}`} />
          <span
            className={`text-xs font-semibold uppercase tracking-wide ${labelClass}`}
          >
            Compilation error
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(saveErrors.message);
                toast.success("Error copied to clipboard");
              } catch {
                toast.error("Copy failed — select and copy manually");
              }
            }}
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded border border-current hover:bg-muted/50 ${labelClass}`}
            title="Copy error message"
          >
            <Copy className="h-3 w-3" />
            <span>Copy</span>
          </button>
          {!isSaving && (
            <button
              type="button"
              onClick={onDismiss}
              className={`text-xs px-2 py-1 rounded hover:bg-muted/50 ${labelClass}`}
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
      <pre
        className={`text-xs font-mono whitespace-pre-wrap break-words max-h-40 overflow-y-auto select-text rounded bg-background/40 border border-current/20 p-2 ${textClass}`}
      >
        {saveErrors.message}
      </pre>

      {/* #12 review card — what the auto-fix loop tried across the 5 attempts. */}
      {saveErrors.attempts && saveErrors.attempts.length > 0 && (
        <div className="mt-2 rounded border border-current/20 bg-background/40 overflow-hidden">
          <div
            className={`flex items-center justify-between px-2 py-1 text-[11px] font-semibold uppercase tracking-wide border-b border-current/10 ${labelClass}`}
          >
            <span>Auto-fix review</span>
            <span className="font-normal normal-case opacity-80">
              {saveErrors.attempts.length} attempt
              {saveErrors.attempts.length === 1 ? "" : "s"} · not resolved
            </span>
          </div>
          <ul className="divide-y divide-current/10">
            {saveErrors.attempts.map((a, i) => {
              const isLast = i === saveErrors.attempts!.length - 1;
              return (
                <li
                  key={a.attempt}
                  className="flex items-start gap-2 px-2 py-1.5 text-xs"
                >
                  <span
                    className={`shrink-0 mt-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold ${labelClass} border border-current/30`}
                  >
                    {a.attempt}
                  </span>
                  <span className={`min-w-0 flex-1 break-words ${textClass}`}>
                    {a.error}
                    {isLast && (
                      <span className="ml-1.5 align-middle inline-block rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide border border-current/40 opacity-80">
                        last error
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
